// src/components/gestionEvento/CategoriasTable.js

import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  IconButton,
  Toolbar,
  Typography,
  Tooltip,
  TextField,
  InputAdornment,
  Box,
  CircularProgress,
  TablePagination, Button, Grid, InputBase, Badge
} from '@mui/material';
import { Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { alpha } from '@mui/material/styles';
import SaveIcon from '@mui/icons-material/Save';
import CategoriasRow from './CategoriasRow'; // Importación por defecto
import PropTypes from 'prop-types';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';

/**
 * Función para calcular el rango de fechas de nacimiento.
 */
const calcularRangoFechasNacimiento = (fechaCorte, edadMin, edadMax) => {
  // Ensure fechaCorte is treated as local date, add time to avoid timezone issues
  const corteDate = fechaCorte ? new Date(fechaCorte + 'T00:00:00') : null;

  if (!corteDate || isNaN(corteDate.getTime())) {
    // console.warn("Fecha de corte inválida para cálculo:", fechaCorte);
    return 'Fecha Inválida';
  }

  const anioCorte = corteDate.getFullYear();

  // Asegurarse que edadMin y edadMax son números válidos
  const numEdadMin = Number(edadMin);
  const numEdadMax = Number(edadMax);

  // Check for valid numbers and non-negative values
  if (isNaN(numEdadMin) || isNaN(numEdadMax) || numEdadMin < 0 || numEdadMax < 0) {
    // console.warn("Edades inválidas para cálculo:", edadMin, edadMax);
    return 'Edad Inválida';
  }
  // Check if min age is greater than max age
  if (numEdadMin > numEdadMax) {
      return 'Edades Invertidas';
  }


  // El de MENOR edad nació MÁS TARDE (año mayor) - usa edadMin
  const fechaNacimientoMaxDate = new Date(anioCorte - numEdadMin, 11, 31); // 31 de diciembre
  // El de MAYOR edad nació MÁS TEMPRANO (año menor) - usa edadMax
  const fechaNacimientoMinDate = new Date(anioCorte - numEdadMax, 0, 1);   // 1 de enero


  const options = { day: '2-digit', month: '2-digit', year: 'numeric' };

  try {
      const minStr = fechaNacimientoMinDate.toLocaleDateString('es-ES', options);
      const maxStr = fechaNacimientoMaxDate.toLocaleDateString('es-ES', options);
      return `${minStr} - ${maxStr}`;
  } catch (e) {
      console.error("Error formateando fechas de nacimiento:", e);
      return "Error Formato";
  }
};

/**
 * Componente para mostrar y gestionar Categorías con selección de filas y paginación.
 */
const CategoriasTable = ({
  competenciaId,
  categorias,
  onUpdateCategoria,
  // onDeleteCategoria, // Ya no se usa directamente aquí para la lógica de UI
  fechaCorte,
  setSnackbar, // Lo mantenemos si otras partes lo usan, pero no para el delete
  onReorderCategorias,
  onRequestDeleteMultiple, // <-- Prop recibida
  onRequestDeleteSingle,   // <-- Prop recibida
  // selectedCategorias: propSelectedCategorias, // Si recibes esto del padre, úsalo
  // setSelectedCategorias: propSetSelectedCategorias, // Si recibes esto del padre, úsalo
}) => {
  const categoriasArray = Array.isArray(categorias) ? categorias : [];

  const [selectedCategorias, setSelectedCategorias] = useState([]);
  const [localSearch, setLocalSearch] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Estados para Paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Estado para Manejo de Carga
  const [isLoading, setIsLoading] = useState(false);

const [dirtyRows, setDirtyRows] = useState({}); // { [id]: { hasChanges, isValid, errors, payload } }

const handleRowDirtyChange = (id, info) => {
  setDirtyRows(prev => {
    const next = { ...prev };
    if (!info?.hasChanges) delete next[id];
    else next[id] = info;
    return next;
  });
};

const dirtyList = Object.entries(dirtyRows);
const dirtyValid = dirtyList.filter(([, v]) => v?.hasChanges && v?.isValid);
const dirtyCount = dirtyList.filter(([, v]) => v?.hasChanges).length;
const bulkMode = dirtyCount >= 2;

const [isBulkSaving, setIsBulkSaving] = useState(false);

const selectionLocked = dirtyCount > 0;

const handleSaveAll = async () => {
  if (dirtyValid.length === 0) return;
  setIsBulkSaving(true);
  let ok = 0;
  const fails = [];

  // Guardado secuencial para no saturar y mantener orden
  for (const [rowId, { payload }] of dirtyValid) {
    try {
      const res = await onUpdateCategoria(competenciaId, rowId, payload);
      if (res?.wasUpdated) {
        ok++;
        // limpiar del mapa
        setDirtyRows(prev => {
          const n = { ...prev };
          delete n[rowId];
          return n;
        });
      } else {
        fails.push({ rowId, reason: res?.reason || 'Error desconocido' });
      }
    } catch (e) {
      fails.push({ rowId, reason: e.message });
    }
  }

  // feedback
  if (ok > 0) {
    setSnackbar?.({ open: true, message: `Guardadas ${ok} categoría(s).`, severity: 'success' });
  }
  if (fails.length > 0) {
    setSnackbar?.({
      open: true,
      message: `Falló guardar ${fails.length}. ${fails.map(f => `#${f.rowId}`).join(', ')}`,
      severity: 'warning',
    });
  }
  setIsBulkSaving(false);
};  

  /**
   * Maneja la selección de todas las filas.
   */
  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      // Filtrar solo las categorías válidas
      const validCategorias = categoriasArray.filter((cat) => cat !== undefined && cat !== null);
      const newSelecteds = validCategorias.map((cat) => cat.id);
      setSelectedCategorias(newSelecteds);
      return;
    }
    setSelectedCategorias([]);
  };

  /**
   * Maneja la selección de una fila individual.
   */
  const handleClick = (event, id) => {
    const selectedIndex = selectedCategorias.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedCategorias, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedCategorias.slice(1));
    } else if (selectedIndex === selectedCategorias.length - 1) {
      newSelected = newSelected.concat(selectedCategorias.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedCategorias.slice(0, selectedIndex),
        selectedCategorias.slice(selectedIndex + 1)
      );
    }

    setSelectedCategorias(newSelected);
  };

  /**
   * Verifica si una categoría está seleccionada.
   */
  const isSelected = (id) => selectedCategorias.indexOf(id) !== -1;

  /**
   * Maneja la eliminación de las categorías seleccionadas.
   */
  const handleDeleteSelected = () => {
    if (selectedCategorias.length === 0) return;
    // Llama a la función del Manager para iniciar el proceso
    onRequestDeleteMultiple(selectedCategorias);
    // Ya no se hace la lógica de confirmación aquí
  };


  /**
   * Maneja cambios en la barra de búsqueda.
   */
React.useEffect(() => {
  // sincroniza el estado local al montar
  setLocalSearch(searchTerm);
}, []);

// debounce: aplica al searchTerm real tras 250 ms
React.useEffect(() => {
  const t = setTimeout(() => setSearchTerm(localSearch), 250);
  return () => clearTimeout(t);
}, [localSearch, setSearchTerm]);

// atajo de teclado Ctrl/⌘ + K para enfocar buscador
const searchRef = React.useRef(null);
React.useEffect(() => {
  const onKey = (e) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    if ((isMac && e.metaKey && e.key.toLowerCase() === 'k') ||
        (!isMac && e.ctrlKey && e.key.toLowerCase() === 'k')) {
      e.preventDefault();
      searchRef.current?.querySelector('input')?.focus();
    }
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
}, []);

  /**
   * Filtra las categorías basándose en el término de búsqueda.
   */
  // filteredCategorias (sin cambios)
  const filteredCategorias = useMemo(() => categoriasArray.filter((cat) => {
    if (!cat) return false;
    const codigoMatch = cat.codigo?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    const nombreMatch = cat.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) || false;
    return codigoMatch || nombreMatch;
  }), [categoriasArray, searchTerm]); // Dependencia agregada: categoriasArray

  /**
   * Calcula las categorías a mostrar en la página actual.
   */
  const paginatedCategorias = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredCategorias.slice(start, end);
  }, [filteredCategorias, page, rowsPerPage]);

  /**
   * Maneja el cambio de página.
   */
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  /**
   * Maneja el cambio en el número de filas por página.
   */
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

    // Sensores para DnD
    const sensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      })
    );
  
    /**
     * Maneja el evento de arrastrar y soltar
     */
  // handleDragEnd (sin cambios)
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active && over && active.id !== over.id) { // Añadir chequeo para active y over
      const oldIndex = categoriasArray.findIndex(cat => cat && cat.id === active.id); // Chequeo cat
      const newIndex = categoriasArray.findIndex(cat => cat && cat.id === over.id); // Chequeo cat

      if (oldIndex === -1 || newIndex === -1) return; // Salir si no se encuentran

      const newCategorias = arrayMove(categoriasArray, oldIndex, newIndex);

      const categoriasConNumeros = newCategorias.map((cat, index) => ({
        ...cat,
        catNumber: index + 1
      }));

      onReorderCategorias(competenciaId, categoriasConNumeros);
    }
  };

    return (
<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Barra de Búsqueda */}
<Box
  sx={{
    position: 'sticky',
    top: 0,
    zIndex: (t) => t.zIndex.appBar,
    px: 2,
    pt: 2,
    pb: 1.5,
    mb:1.25,
    bgcolor: 'background.default',
    backdropFilter: 'blur(6px)',
    borderBottom: (t) => `1px solid ${t.palette.divider}`,
    borderRadius: 3,
  }}
>
  <Grid container spacing={1.5} alignItems="center">
    {/* Buscador */}
    <Grid item xs={12} md={7} lg={8}>
      <Box
        ref={searchRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          borderRadius: 999,
          px: 1,
          py: 0.5,
          bgcolor: (t) => alpha(t.palette.background.paper, 0.8),
          border: (t) => `1px solid ${alpha(t.palette.text.primary, 0.08)}`,
          boxShadow: (t) => `0 2px 10px ${alpha(t.palette.common.black, 0.06)}`,
          transition: 'box-shadow .2s, transform .05s',
          '&:focus-within': {
            boxShadow: (t) => `0 6px 22px ${alpha(t.palette.primary.main, 0.18)}`,
          },
        }}
      >
        <SearchIcon sx={{ mx: 1, opacity: 0.7 }} />
        <InputBase
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          placeholder="Buscar por código o nombre… (Ctrl/⌘ + K)"
          inputProps={{ 'aria-label': 'buscar categorías' }}
          sx={{
            flex: 1,
            fontSize: { xs: '0.85rem', sm: '0.9rem' },
            py: 0.5,
          }}
        />
        {!!localSearch && (
          <IconButton
            size="small"
            onClick={() => setLocalSearch('')}
            aria-label="limpiar búsqueda"
            sx={{ mr: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
    </Grid>

    {/* Acciones */}
    <Grid
      item
      xs={12}
      md={5}
      lg={4}
      sx={{
        display: 'flex',
        justifyContent: { xs: 'flex-start', md: 'flex-end' },
        gap: 1,
        mt: { xs: 1, md: 0 },
      }}
    >
      {/* Botón eliminar seleccionadas (soft) */}
      <Badge
        color="error"
        badgeContent={selectedCategorias.length || 0}
        overlap="circular"
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
    {selectedCategorias.length > 0 && dirtyCount === 0 && (
    <Button
      onClick={handleDeleteSelected}
      startIcon={<DeleteIcon />}
      fullWidth
      sx={(t) => ({
        borderRadius: 999,
        px: { xs: 1.25, sm: 1.75 },
        py: 1,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: { xs: '0.8rem', sm: '0.875rem' },
        bgcolor: alpha(t.palette.error.main, 0.08),
        color: t.palette.error.main,
        border: `1px solid ${alpha(t.palette.error.main, 0.24)}`,
        '&:hover': { bgcolor: alpha(t.palette.error.main, 0.12) },
      })}
    >
      Eliminar seleccionadas ({selectedCategorias.length})
    </Button>
  )}
{bulkMode && (
  <Button
    onClick={handleSaveAll}
    startIcon={<SaveIcon />}
    disabled={isBulkSaving || dirtyValid.length === 0}
    sx={(t) => ({
      borderRadius: 999,
      px: { xs: 1.25, sm: 1.75 },
      py: 1,
      textTransform: 'none',
      fontWeight: 600,
      fontSize: { xs: '0.8rem', sm: '0.875rem' },
      bgcolor: alpha(t.palette.success.main, 0.08),
      color: t.palette.success.main,
      border: `1px solid ${alpha(t.palette.success.main, 0.24)}`,
      '&:hover': { bgcolor: alpha(t.palette.success.main, 0.12) },
      '&.Mui-disabled': { bgcolor: alpha(t.palette.text.primary, 0.05) },
    })}
  >
    {isBulkSaving ? 'Guardando…' : `Guardar (${dirtyValid.length})`}
  </Button>
)}
      </Badge>

      {/* (Opcional) Botón “Limpiar selección” si quieres otro CTA */}
      {/* 
      <Button
        onClick={() => setSelectedCategorias([])}
        sx={{
          borderRadius: 999,
          px: { xs: 1.25, sm: 1.75 },
          py: 1,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: { xs: '0.8rem', sm: '0.875rem' },
          bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          color: 'primary.main',
          border: (t) => `1px solid ${alpha(t.palette.primary.main, 0.2)}`,
          '&:hover': {
            bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
          },
        }}
      >
        Limpiar selección
      </Button>
      */}
    </Grid>
  </Grid>
</Box>
  
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
<TableContainer
  component={Paper}
  sx={{
    borderRadius: 3,
    boxShadow: 6,
    overflowX: 'auto',
    overflowY: 'auto',
    maxHeight: { xs: '150vh', md: '150vh' },
  }}
>
  <Table
    stickyHeader
    size="medium"
    sx={{
      minWidth: 650,
      // tamaño de texto y padding responsivos para toda la tabla
      '& th, & td': {
        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
        py: { xs: 0.5, sm: 0.75, md: 1 },   // padding vertical
        px: { xs: 1,   sm: 1.25, md: 1.5 }, // padding horizontal
        lineHeight: 1.25,
      },
      '& thead th': {
        bgcolor: 'background.paper',
        fontWeight: 600,
        letterSpacing: 0.2,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        fontSize: { xs: '0.78rem', sm: '0.85rem', md: '0.95rem' }, // un poco más grande para header
      },
      '& tbody tr:hover': { bgcolor: 'action.hover' },
      '& tbody tr:nth-of-type(odd)': { bgcolor: 'action.selectedOpacity' },

      // Compactar todos los TextField internos en móvil
      '& .MuiTextField-root': {
        width: '100%',
      },
      '& .MuiInputBase-root': {
        height: { xs: 36, sm: 38, md: 40 }, // alto del input
      },
      '& .MuiInputBase-input': {
        fontSize: { xs: '0.75rem', sm: '0.8125rem', md: '0.875rem' },
        py: { xs: 0.75, sm: 0.875, md: 1 }, // acolchado interno
      },

      // IconButtons (drag, guardar, eliminar) más compactos en móvil
      '& .MuiIconButton-root': {
        padding: { xs: 0.25, sm: 0.5, md: 0.75 },
      },
      // Celda del drag handle: angosta en móvil
      '& td:first-of-type': {
        width: { xs: 40, sm: 44, md: 48 },
      },
      // Checkbox cell también más angosta
      '& td.MuiTableCell-paddingCheckbox': {
        width: { xs: 40, sm: 44, md: 48 },
      },
    }}
  >
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
<Checkbox
  indeterminate={
    !selectionLocked &&
    selectedCategorias.length > 0 &&
    selectedCategorias.length < filteredCategorias.length
  }
  checked={
    !selectionLocked &&
    filteredCategorias.length > 0 &&
    selectedCategorias.length === filteredCategorias.length
  }
  onChange={handleSelectAllClick}
  inputProps={{ 'aria-label': 'seleccionar todas las categorías' }}
  disabled={selectionLocked || filteredCategorias.length === 0} // <-- bloqueado si hay edición
/>
                </TableCell>
                    <TableCell>Orden</TableCell>
                <TableCell>#Cat</TableCell>
                <TableCell>Código</TableCell>
                <TableCell>Nombre Categoria</TableCell>
                <TableCell>Edad Mínima</TableCell>
                <TableCell>Edad Máxima</TableCell>
                <TableCell>Rango de Fechas de Nac</TableCell>
                <TableCell>Fecha de Corte</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
            <SortableContext
               // Usar los IDs de las categorías paginadas para SortableContext
              items={paginatedCategorias.map(cat => cat?.id).filter(Boolean)} // Asegurarse de que los IDs existen
              strategy={verticalListSortingStrategy}
            >
{paginatedCategorias.map((categoria, idx) => {
  if (!categoria || !categoria.id) return null;
  return (
    <CategoriasRow
      key={categoria.id}
      id={categoria.id}
      competenciaId={competenciaId}
      categoria={categoria}
      onUpdateCategoria={onUpdateCategoria}
      isSelected={isSelected(categoria.id)}
      handleClick={handleClick}
      setSnackbar={setSnackbar}
      onRequestDeleteSingle={onRequestDeleteSingle}
      onRowDirtyChange={handleRowDirtyChange}
      bulkMode={bulkMode}
      selectionLocked={selectionLocked}
      rowIndex={page * rowsPerPage + idx}   // 👈 nuevo
    />
  );
})}
              {paginatedCategorias.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    No hay categorías para mostrar.
                  </TableCell>
                </TableRow>
              )}
            </SortableContext>
            </TableBody>
          </Table>
  
          {/* Componente de Paginación */}
          <TablePagination
            component="div"
            count={filteredCategorias.length} // Usar longitud de filtradas
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
          />
        </TableContainer>
        </DndContext>
  
        {/* Pantalla de Carga (si aún se necesita localmente) */}
        {isLoading && (
          <Box
            position="fixed"
            inset={0}
            display="flex"
            alignItems="center"
            justifyContent="center"
            bgcolor="rgba(0,0,0,0.3)"
            zIndex={1300}
          >
            <CircularProgress size={60} />
          </Box>
        )}
      </Box>
    );
  };
  
  CategoriasTable.propTypes = {
    competenciaId: PropTypes.string.isRequired,
    categorias: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        catNumber: PropTypes.number, // Puede ser null si se calcula después
        codigo: PropTypes.string,
        nombre: PropTypes.string,
        edadMin: PropTypes.number,
        edadMax: PropTypes.number,
        fechaCorte: PropTypes.string,
      })
    ).isRequired,
    onUpdateCategoria: PropTypes.func.isRequired,
    // onDeleteCategoria: PropTypes.func.isRequired, // Quitar si ya no se pasa
    fechaCorte: PropTypes.string, // Hacer opcional si se puede obtener de otro lado
    setSnackbar: PropTypes.func.isRequired,
    // setSelectedCategorias: PropTypes.func, // Quitar si se maneja localmente
    onReorderCategorias: PropTypes.func.isRequired,
    onRequestDeleteMultiple: PropTypes.func.isRequired, // <-- Prop requerida
    onRequestDeleteSingle: PropTypes.func.isRequired,   // <-- Prop requerida
  };
  
  // Poner valores por defecto si es necesario (ej. fechaCorte)
  CategoriasTable.defaultProps = {
    fechaCorte: '', // O un valor por defecto más adecuado
  };
  
  
  export default CategoriasTable;
